import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import Icons from '@/const/icons';
import { useImagePreview } from '@/hooks/useImagePreview';
import useModal from '@/hooks/useModal';
import { X } from 'lucide-react';
import { useNotification } from '@/hooks';
import clsx from 'clsx';
import { logApiError } from '@/lib/logApiError';
import { usePathname } from 'next/navigation';
import { Button } from '../button';
import { Icon } from '../icon';
import { ProgressBarBox } from '../progress-bar-box';
import { Typography } from '../typography';

const ZERO_PERCENT = 0;
const NINETY_PERCENT = 90;
const ONE_HUNDRED_PERCENT = 100;
interface Props {
	file: File | null;
	setFile: (value: File | null) => void;
	isOpen: boolean;
	prevFile: string | null;
	setPrevFile: (value: string | null) => void;
	rules?: Record<string, unknown>;
	register: UseFormRegister<FieldValues>;
	setImage: (url: string | null, file: File | null) => void;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
	avoidDimensionsValidation?: boolean;
	customToastSuccess?: string;
	maxFileSizeInMB?: number;
}

export const ModalUploadImage: React.FC<Props> = ({
	file,
	setFile,
	isOpen,
	prevFile,
	rules,
	setPrevFile,
	register,
	setImage,
	setIsModalOpen,
	avoidDimensionsValidation = false,
	customToastSuccess,
	maxFileSizeInMB = 25,
}) => {
	const pathname = usePathname();
	const { toastError, toastSuccess } = useNotification();
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState<string | React.ReactNode>();
	const refInputFile = useRef<HTMLInputElement>(null);
	const { loadImage } = useImagePreview();
	const { Modal, show, hide } = useModal();

	useEffect(() => {
		if (isOpen) {
			show();
			setError('');
		} else {
			setProgress(0);
			hide();
		}
	}, [isOpen]);

	const onClickUpload = (): void => {
		refInputFile?.current?.click();
	};

	const simulateProgress = async (
		start: number,
		max: number
	): Promise<boolean> => {
		return new Promise((resolve) => {
			let progress = start;
			const interval = setInterval(() => {
				progress += 10;
				setProgress(progress);
				if (progress >= max) {
					clearInterval(interval);
					resolve(true);
				}
			}, 300);
		});
	};

	const validateImageSize = (file: File): boolean => {
		if (file.size > maxFileSizeInMB * 1000 * 1024) {
			setProgress(0);
			setError(
				`The file you tried to upload is too heavy. Please try again with a file up to ${maxFileSizeInMB} MB.`
			);
			logApiError({
				status: 400,
				message: 'The file you tried to upload is too heavy.',
				config: { data: '', url: pathname },
			});
			return false;
		}

		return true;
	};

	const validateImageFormat = (file: File): boolean => {
		if (
			file.type !== 'image/jpeg' &&
			file.type !== 'image/jpg' &&
			file.type !== 'image/png'
		) {
			setProgress(0);
			setError(
				'Error Unsupported Format: Invalid image format. Please upload the image in JPG, JPEG or PNG format.'
			);
			logApiError({
				status: 400,
				message:
					'Error Unsupported Format: Invalid image format. Please upload the image in JPG, JPEG or PNG format.',
				config: { data: '', url: pathname },
			});
			return false;
		}

		return true;
	};

	const handleImageChange = async (
		e: React.ChangeEvent<HTMLInputElement>
	): Promise<void> => {
		const file = e.target.files?.[0];
		setError('');

		if (!file || !validateImageSize(file) || !validateImageFormat(file)) return;

		try {
			simulateProgress(ZERO_PERCENT, NINETY_PERCENT).then(() => {
				loadImage(file)?.then((data) => {
					if (
						(data.width >= 900 && data.height >= 345) ||
						avoidDimensionsValidation
					) {
						setImage(data.imageSrc, file);
						setProgress(ONE_HUNDRED_PERCENT);
						toastSuccess(
							customToastSuccess ||
								'Your image has been uploaded successfully. Please remember to save your changes.'
						);
					} else {
						handleRemoveFile();
						setError(
							<>
								Error Invalid Dimensions: <br /> The image must be no smaller
								than 900x345 pixels. Please upload an image with the correct
								dimensions.
							</>
						);
					}
				});
			});
		} catch (error) {
			toastError('Error: Your image could not be uploaded. Please try again.');
		} finally {
			if (e.target) {
				e.target.value = '';
			}
		}
	};

	const handleDropFile = async (e: React.DragEvent): Promise<void> => {
		e.preventDefault();
		const newFile = e.dataTransfer.files[0];
		if (file) return; // this is to avoid a visual bug when the user uploads a new image when there is already an image
		if (
			!newFile ||
			!validateImageSize(newFile) ||
			!validateImageFormat(newFile)
		)
			return;

		try {
			simulateProgress(ZERO_PERCENT, NINETY_PERCENT).then(() => {
				loadImage(newFile)?.then((data) => {
					if (data.width >= 900 && data.height >= 345) {
						setImage(data.imageSrc, newFile);
						setProgress(ONE_HUNDRED_PERCENT);
						toastSuccess(
							customToastSuccess ||
								'Your image has been uploaded successfully. Please remember to save your changes.'
						);
					} else {
						handleRemoveFile();
						setError(
							<>
								Error Invalid Dimensions: <br /> The image must be no smaller
								than 900x345 pixels. Please upload an image with the correct
								dimensions.
							</>
						);
					}
				});
			});
		} catch (error) {
			toastError('Error: Your image could not be uploaded. Please try again.');
		}
	};

	const handleRemoveFile = (): void => {
		setProgress(0);
		setPrevFile(null);
		setImage(null, null);
		setFile(null);
	};

	const registerAux = register('file', rules);

	const getFileName = (): string => {
		if (file && typeof file !== 'string' && 'name' in file) return file.name;

		if (prevFile || typeof file === 'string') return 'image';

		return '';
	};

	return (
		<Modal classNameCardContainer="!bg-gradient-to-r from-white to-white">
			<div className="w-full md:w-[451px] flex flex-col items-center space-y-6">
				<div id="header" className="space-y-3 w-full relative">
					<div className="flex items-center justify-center">
						<Icon src={Icons.imageCards} className="!w-16" />
						<Typography type="semibold-20">Upload image</Typography>
						<button
							className="absolute right-0 top-2"
							onClick={() => setIsModalOpen(false)}
						>
							<X />
						</button>
					</div>
				</div>
				<div
					id="content"
					className={clsx(
						'md:px-10 border-1 border-opacity-80 rounded-[32px] border border-dashed bg-gray-20',
						{
							'border-opacity-primary': !error,
							'border-red-600': error,
						}
					)}
					onDrop={handleDropFile}
					onDragOver={(e) => e.preventDefault()}
				>
					<div className="py-6 px-8 flex flex-col items-center gap-8">
						<div>
							<Typography type="regular-14" className="opacity-60 text-center">
								Drag and drop your file here, or click to
							</Typography>
							<Typography type="regular-14" className="opacity-60 text-center">
								browse from your device
							</Typography>
						</div>
						{(progress > 0 || file || prevFile) && (
							<div className="w-48">
								<ProgressBarBox
									percent={progress}
									handleRemove={handleRemoveFile}
									fileName={getFileName()}
									isShowPrevFile={false}
								/>
							</div>
						)}
						{progress === 0 && !file && (
							<Button
								size="small"
								decoration="primary"
								className="flex items-center gap-2 !h-[30px] !py-4"
								onClick={onClickUpload}
							>
								Upload
								<Icon src={Icons.documentUpload} className="!w-4 !h-4" />
							</Button>
						)}

						{error && (
							<Typography type="regular-12" className="text-red-600 px-6">
								{error}
							</Typography>
						)}
						<input
							{...registerAux.ref}
							ref={refInputFile}
							type="file"
							accept="image/jpeg, image/jpg, image/png"
							className="hidden"
							onChange={handleImageChange}
						/>
					</div>
				</div>
			</div>
		</Modal>
	);
};
